import FilterChip from 'LuxKit/Chips/FilterChip'
import Group from 'components/utils/Group'
import React, { useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import { FlightDealsCarouselFilterItem } from './FlightDealsCarouselFilterItem'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import useToggle from 'hooks/useToggle'
import { ALL_KEY, FilterType } from './FlightDealsCarousel'
import type { FlightsClassTypes } from 'constants/flight'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'

const FiltersContainer = styled(Group)`
  padding: 0 ${rem(16)};
`

interface Filter {
  label: string
  value: string
  price?: number
}

interface Props {
  airlines: Array<Filter>
  cabins: Array<Filter>
  selectedAirlines: Set<string>
  selectedCabins: Set<string>
  handleToggle: (type: FilterType, value: string) => void
}

export function FlightDealsCarouselStaticFilterPills(props: Props) {
  const { airlines, cabins, selectedAirlines, selectedCabins, handleToggle } = props
  const airlinesDropdownTrigger = useRef<HTMLButtonElement>(null)
  const cabinsDropdownTrigger = useRef<HTMLButtonElement>(null)
  const { value: showAirlinesFilter, toggle: toggleAirlinesFilter } = useToggle()
  const { value: showCabinsFilter, toggle: toggleCabinsFilter } = useToggle()

  const intl = useIntl()
  const { fareCabinLabelMap } = useFlightFareLabels()

  const showAirlinesNum = selectedAirlines.size > 0 && !selectedAirlines.has(ALL_KEY)
  const showCabinsNum = selectedCabins.size > 0 && !selectedCabins.has(ALL_KEY)

  const allAirlinesLabel = intl.formatMessage({
    id: 'I17IPDY0XW',
    defaultMessage: 'All airlines',
    description: 'Filter chip label showing all airlines in the search or listing filters panel',
  })
  const airlinesLabel = intl.formatMessage({
    id: 'xpA+qN2EcF',
    defaultMessage: 'Airlines',
    description: 'Filter chip label for airlines with count in the filters panel',
  })
  const allCabinsLabel = intl.formatMessage({
    id: 'YyrqzXAHDH',
    defaultMessage: 'All cabins',
    description: 'Filter chip label showing all cabins in the search or listing filters panel',
  })
  const cabinsLabel = intl.formatMessage({
    id: 'aZve2Gre5L',
    defaultMessage: 'Cabins',
    description: 'Filter chip label for cabins with count in the search or listing filters panel',
  })

  return (
    <>
      <FilterChip
        size="medium"
        ref={airlinesDropdownTrigger}
        onClick={toggleAirlinesFilter}
        endIcon={showAirlinesFilter ? <LineAngleUpIcon /> : <LineAngleDownIcon />}
      >
        {!showAirlinesNum ? allAirlinesLabel : airlinesLabel} {showAirlinesNum && intl.formatMessage(
          {
            id: 'RVjuI2XrzV',
            defaultMessage: '({count, number})',
            description: 'Selected filter count in parentheses in the search or listing filters panel',
          },
          { count: selectedAirlines.size },
        )}
      </FilterChip>

      <FilterChip
        size="medium"
        ref={cabinsDropdownTrigger}
        endIcon={showAirlinesFilter ? <LineAngleUpIcon /> : <LineAngleDownIcon />}
        onClick={toggleCabinsFilter}
      >
        {!showCabinsNum ? allCabinsLabel : cabinsLabel} {showCabinsNum && intl.formatMessage(
          {
            id: 'RVjuI2XrzV',
            defaultMessage: '({count, number})',
            description: 'Selected filter count in parentheses in the search or listing filters panel',
          },
          { count: selectedCabins.size },
        )}
      </FilterChip>

      <DropdownList
        size="M"
        open={showAirlinesFilter}
        triggerRef={airlinesDropdownTrigger}
        anchorRef={airlinesDropdownTrigger}
        onClose={toggleAirlinesFilter}
        placement="bottom-start"
      >
        <FiltersContainer direction="vertical">
          <VerticalSpacer gap={16}>
            <FlightDealsCarouselFilterItem
              checked={selectedAirlines.has(ALL_KEY)}
              onCheckedChange={() => handleToggle(FilterType.AIRLINES, ALL_KEY)}
              label={intl.formatMessage({
                id: 'Pj41CERbOL',
                defaultMessage: 'All airlines',
                description: 'Filter label for all airlines option in the search or listing filters panel',
              })}
            />

            {airlines.map(airline => (
              <FlightDealsCarouselFilterItem
                key={airline.value}
                checked={selectedAirlines.has(airline.value)}
                onCheckedChange={() => handleToggle(FilterType.AIRLINES, airline.value)}
                label={airline.label}
                price={airline.price}
              />
            ))}
          </VerticalSpacer>
        </FiltersContainer>
      </DropdownList>

      <DropdownList
        size="M"
        open={showCabinsFilter}
        triggerRef={cabinsDropdownTrigger}
        anchorRef={cabinsDropdownTrigger}
        onClose={toggleCabinsFilter}
        placement="bottom-start"
      >
        <FiltersContainer direction="vertical">
          <VerticalSpacer gap={16}>
            <FlightDealsCarouselFilterItem
              checked={selectedCabins.has(ALL_KEY)}
              onCheckedChange={() => handleToggle(FilterType.CABINS, ALL_KEY)}
              label={intl.formatMessage({
                id: 'XvYMb9+1SH',
                defaultMessage: 'All cabins',
                description: 'Filter label for all cabins option in the search or listing filters panel',
              })}
            />

            {cabins.map(cabin => (
              <FlightDealsCarouselFilterItem
                key={cabin.value}
                checked={selectedCabins.has(cabin.value)}
                onCheckedChange={() => handleToggle(FilterType.CABINS, cabin.value)}
                label={fareCabinLabelMap[cabin.value as FlightsClassTypes]}
                price={cabin.price}
              />
            ))}
          </VerticalSpacer>
        </FiltersContainer>
      </DropdownList>
    </>
  )
}
